import React, { useEffect, useState, useCallback } from 'react';

import { NavLink, useHistory } from 'react-router-dom';

import EmailField from 'elements/statefull/emailField';
import PasswordField from 'elements/statefull/passwordField';

import Card from 'components/card';

import configs from 'configs';
import api from 'helpers/api';
import toaster from 'helpers/toaster';
import { enterKeyCode } from 'constants/enterKey';
import { dispatchLoginEvent } from 'helpers/events';
import getQueryVariable from 'helpers/urlQueryParamsParser';

import { INTERCOM_ID } from '../../constants';
import { useMount } from '../../hooks';

const SignIn = () => {

  const history = useHistory();

  const [state, setState] = useState({
    email: '',
    password: '',
    errors: {},
    needsToLoad: false,
    showPassword: false,
  });

  const changeHandler = useCallback(event => {
    const { dataset, value } = event.target;

    setState(prev => ({
      ...prev,
      [dataset.key]: value.trim(),
    }));
  }, []);

  const setErrorsHandler = useCallback((hasErrors, key) => {
    setState(prev => ({
      ...prev,
      errors: {
      ...prev.errors,
      [key]: hasErrors,
      }
    }));
  }, []);

  const handlePostLogin = useCallback(returnUrl => {
    if (returnUrl) {
      window.location.href = returnUrl;
    } else {
      dispatchLoginEvent();
      history.push('/');
    }
  }, [history]);

  const hasErrors = useCallback(() => {
    Object.values(state.errors).some(error => error)
  }, [state.errors]);

  const submit = useCallback(() => {
    const { email, password, needsToLoad } = state;

    if (email && password && !hasErrors()) {
      setState(prev => ({ ...prev, needsToLoad: true }));

      const { returnUrl } = configs;
      const options = {
        email,
        password,
      };

      if (returnUrl) {
        options.returnUrl = returnUrl;
      }

      !needsToLoad && api.create('/auth/login', options).then(response => {
        setState(prev => ({ ...prev, needsToLoad: false }));

        if (response) {
          const { requestTwoFactor, qrUrl, requestEmailConfirmation } = response || {};

          if (requestEmailConfirmation) {
            history.push('/registration', { emailNotConfirmed: true,email });
            return;
          }

          configs.authenticated = true;
          window.sessionStorage.setItem('authenticated', true);

          if (requestTwoFactor) {
            history.push('/authentication', { email, returnUrl, qrUrl });
          } else {
            const analytics = window.analytics;

            if (analytics) {
              const anonymousId = analytics.user().anonymousId();

              return analytics.alias(email, anonymousId, {}, () => {
                analytics.identify(anonymousId, { email });
                handlePostLogin(returnUrl);
              });
            }
            return handlePostLogin(returnUrl);
          }
        }
      })
    }
  }, [handlePostLogin, hasErrors, history, state]);

  const keyPressHandler = useCallback(event => {
    if (event.keyCode === enterKeyCode || event.which === enterKeyCode) {
      submit();
    }
  }, [submit]);

  useEffect(() => {
    if (configs.isLoggedIn) {
      history.push('/');
    }
  }, [history]);

  useEffect(() => {
    const query = history.location.state
      ? history.location.state.from.search
      : history.location.search;
    const emailConfirmed = getQueryVariable(query, 'emailConfirmed');
    const expired = getQueryVariable(query, 'expired');
    const mainSiteUrl = getQueryVariable(query, 'mainSiteUrl');

    if (expired) {
      toaster('Email link is expired');
      return;
    }

    if (emailConfirmed) {
      toaster('Email confirmed. Please sign in', true);

      if (mainSiteUrl !== '/' && mainSiteUrl) {
        window.location.href = mainSiteUrl;
      }
    }
  }, [history]);

  const { email, password } = state;
  const isDisabled = !email || !password || hasErrors();

  useMount(() => {
    if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_CUSTOM_NODE_ENV === 'production') {
      window.Intercom && window.Intercom('boot', {
        app_id: INTERCOM_ID,
        name: null,
        email: null
      });
    }
  })

  return (
    <Card
      title="Sign In"
      subTitle={
        <>
          {'New to BetConstruct? '}
          <NavLink to='/registration'>Sign Up</NavLink>
        </>
      }
      button={{
        title: "Sign In",
        onClick: submit,
        disabled: isDisabled,
        needsToLoad: state.needsToLoad,
      }}
    >
      <EmailField
        data-key="email"
        errorKey="email"
        value={state.email}
        setErrors={setErrorsHandler}
        onChange={changeHandler}
        onKeyPress={keyPressHandler}
      />
      <PasswordField
        noValidate
        name="Password"
        errorKey="password"
        data-key="password"
        setErrors={setErrorsHandler}
        value={state.password}
        onChange={changeHandler}
        onKeyPress={keyPressHandler}
      />
      <div className="cf-note-link-holder password-footer">
        <NavLink to='/resetPassword'>Forgot password?</NavLink>
      </div>
    </Card>
  );
};

export default SignIn;
